import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { TRIP_FEEDBACK_NOTIFICATION_HELP } from '../shared/tripFeedbackNotificationHelp';

const TRIP_FEEDBACK_HELP_KEYS = ['client.post_trip_reminder', 'staff.low_rating', 'driver.tip.received'];

export default function TripFeedbackSettings({ orgId }) {
    const [minutes, setMinutes] = useState(120);
    const [remindersOn, setRemindersOn] = useState(true);
    const [sms,setSms]=useState(true);const [quietStart,setQuietStart]=useState('21:00');const [quietEnd,setQuietEnd]=useState('08:00');const [origin,setOrigin]=useState('');
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    useEffect(() => {
        let active = true;
        supabaseClient.from('org_settings').select('post_trip_reminder_delay_minutes,post_trip_reminder_enabled,post_trip_sms_enabled,post_trip_quiet_start,post_trip_quiet_end,client_app_origin').eq('org_id', orgId).maybeSingle()
            .then(({ data, error: failure }) => {
                if (!active) return;
                if (failure) setError(failure.message);
                else {
                    setMinutes(data?.post_trip_reminder_delay_minutes ?? 120);
                    setRemindersOn(data?.post_trip_reminder_enabled ?? true);
                    setSms(data?.post_trip_sms_enabled??true);setQuietStart(data?.post_trip_quiet_start||'21:00');setQuietEnd(data?.post_trip_quiet_end||'08:00');setOrigin(data?.client_app_origin||'');
                }
                setBusy(false);
            }).catch((failure) => {
                if (active) { setError(`Reminder delay could not be loaded: ${failure.message}`); setBusy(false); }
            });
        return () => { active = false; };
    }, [orgId]);
    async function save(event) {
        event.preventDefault();
        setSaved('');
        const value = Number(minutes);
        if (!Number.isInteger(value) || value < 0 || value > 10080) {
            setError('Choose a whole number of minutes from 0 to 10080.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const { error: failure } = await supabaseClient.from('org_settings')
                .upsert({ org_id: orgId, post_trip_reminder_delay_minutes: value,post_trip_sms_enabled:sms,post_trip_quiet_start:quietStart,post_trip_quiet_end:quietEnd,client_app_origin:origin||null }, { onConflict: 'org_id' });
            if (failure) throw failure;
            setSaved('Reminder delay saved.');
        } catch (failure) { setError(`Reminder delay could not be saved: ${failure.message}`); }
        finally { setBusy(false); }
    }
    async function saveSwitch(next) {
        setSaved('');
        setBusy(true);
        setError('');
        try {
            const { error: failure } = await supabaseClient.from('org_settings')
                .upsert({ org_id: orgId, post_trip_reminder_enabled: next }, { onConflict: 'org_id' });
            if (failure) throw failure;
            setRemindersOn(next);
            setSaved(next ? 'Rating reminders are on.' : 'Rating reminders are off.');
        } catch (failure) { setError(`The reminder setting could not be saved: ${failure.message}`); }
        finally { setBusy(false); }
    }
    return (
        <section className="rounded-lg border p-4 space-y-3" aria-label="Post-trip feedback">
            <h3 className="font-semibold">Post-trip feedback</h3>
            <label className="flex items-center gap-2">
                <input id="post-trip-reminder-enabled" type="checkbox" role="switch" checked={remindersOn} disabled={busy}
                    onChange={(event) => saveSwitch(event.target.checked)} />
                Send a rating reminder after each completed trip
            </label>
            {!remindersOn && <p className="text-sm">Reminders are off. No rating reminders are sent.</p>}
            <form onSubmit={save} className="flex flex-wrap items-center gap-3">
                <label htmlFor="post-trip-delay">Send the rating reminder this many minutes after completion</label>
                <input id="post-trip-delay" type="number" min="0" max="10080" step="1" required
                    value={minutes} disabled={busy} onChange={(event) => { setMinutes(event.target.value); setSaved(''); }} />
                <button className="px-3 py-2 rounded border" disabled={busy} type="submit">Save reminder delay</button>
            </form>
            <label className="flex items-center gap-2"><input type="checkbox" checked={sms} disabled={busy} onChange={event=>setSms(event.target.checked)} />Allow a text rating reminder</label>
            <div className="rf-grid"><label>Quiet hours start<input type="time" value={quietStart} onChange={event=>setQuietStart(event.target.value)} /></label><label>Quiet hours end<input type="time" value={quietEnd} onChange={event=>setQuietEnd(event.target.value)} /></label></div>
            <label>Client app address (optional override)<input type="url" placeholder="https://your-client-app.example" value={origin} onChange={event=>setOrigin(event.target.value)} /></label>
            <p className="text-sm">Default delay: 120 minutes (2 hours). Quiet hours use the company time zone. Save reminder delay also saves these choices.</p>
            {error && <p role="alert" className="text-red-700">{error}</p>}
            {saved && <p role="status">{saved}</p>}
            {TRIP_FEEDBACK_HELP_KEYS.map((key) => [key, TRIP_FEEDBACK_NOTIFICATION_HELP[key]]).map(([key, help]) => (
                <details key={key}>
                    <summary>{key}</summary>
                    <p>{help.definition}</p>
                    <p><strong>Who gets it: </strong>{help.who}</p>
                    <p><strong>When it is sent: </strong>{help.when}</p>
                </details>
            ))}
        </section>
    );
}
